import React, { useEffect, useRef } from 'react';
import {
  Text,
  VStack,
  Box,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  RadioGroup,
  Radio,
  FormErrorMessage,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import DateField from 'components/fields/DateField';
import { useAppSelector, useAppDispatch } from 'hooks';
import getDateFromString from 'helpers/getDateFromString';
import getDateString from 'helpers/getDateString';
import {
  setMarketingApprovalDate,
  setMarketingApprovalTicket,
  setChecklistItem,
  removeChecklistItem,
  setSubmissionType,
  setCoverageBoundMethod,
} from 'reducers/commercialFormReducer';
import { useCommercialAutoSigningEnabled } from 'hooks/useCommercialAutoSigningEnabled';

interface CommercialChecklistProps {
  isLocked?: boolean;
}

const CommercialChecklist = ({
  isLocked = false,
}: CommercialChecklistProps) => {
  const dispatch = useAppDispatch();
  const lineOfBusiness = useAppSelector(
    (state) => state.commercialForm.lineOfBusiness,
  );
  const marketingApprovalDate = useAppSelector(
    (state) =>
      state.commercialForm.data.submission_information.marketing_approval_date,
  );
  const marketingApprovalTicket = useAppSelector(
    (state) =>
      state.commercialForm.data.submission_information.support_ticket_number,
  );
  const checklistData = useAppSelector(
    (state) =>
      state.commercialForm.data.submission_information.checklist_questions ||
      {},
  );
  const submissionType = useAppSelector(
    (state) => state.commercialForm.data.submission_information.submission_type,
  );
  const coverageBoundMethod = useAppSelector(
    (state) =>
      state.commercialForm.data.submission_information.how_was_coverage_bound,
  );
  const validationErrors = useAppSelector(
    (state) => state.commercialForm.validationErrors,
  );
  const isCommercialAutoSigningEnabled = useCommercialAutoSigningEnabled();

  const prevSubmissionTypeRef = useRef(submissionType);
  const prevCoverageBoundMethodRef = useRef(coverageBoundMethod);
  const checklistDataRef = useRef(checklistData);

  // Keep checklistData ref updated
  useEffect(() => {
    checklistDataRef.current = checklistData;
  }, [checklistData]);

  // Clear irrelevant checklist items and related fields when options change
  useEffect(() => {
    const submissionTypeChanged =
      prevSubmissionTypeRef.current !== submissionType &&
      prevSubmissionTypeRef.current;
    const coverageBoundMethodChanged =
      prevCoverageBoundMethodRef.current !== coverageBoundMethod &&
      prevCoverageBoundMethodRef.current;

    if (submissionTypeChanged || coverageBoundMethodChanged) {
      // Helper function to determine if a checklist item is relevant to current selection
      const isItemRelevant = (itemKey: string): boolean => {
        // Property & Casualty items
        if (lineOfBusiness === 'Property & Casualty') {
          if (submissionType === 'non_portal') {
            return itemKey.startsWith('pc_non_portal_');
          } else if (submissionType === 'portal_phone') {
            return itemKey.startsWith('pc_portal_');
          }
          return false;
        }

        // Auto items
        if (lineOfBusiness === 'Auto') {
          if (submissionType === 'irca') {
            // For IRCA, it depends on coverage bound method
            if (
              coverageBoundMethod === 'applied_rating_email' ||
              coverageBoundMethod === 'applied_rating_portal'
            ) {
              return itemKey.startsWith('auto_irca_light_');
            } else if (coverageBoundMethod === 'email_email') {
              return itemKey.startsWith('auto_irca_email_');
            }
            return false;
          } else if (submissionType === 'fleet') {
            return itemKey.startsWith('auto_fleet_');
          } else if (submissionType === 'garage') {
            return itemKey.startsWith('auto_garage_');
          }
          return false;
        }

        return false;
      };

      // Clear all checklist items that are not relevant to current selection
      Object.keys(checklistDataRef.current).forEach((key) => {
        if (!isItemRelevant(key)) {
          dispatch(removeChecklistItem(key));
        }
      });

      // Helper function to check if marketing approval fields are relevant
      const isMarketingApprovalRelevant = (): boolean => {
        if (lineOfBusiness === 'Property & Casualty') {
          return (
            submissionType === 'non_portal' &&
            (checklistDataRef.current['pc_non_portal_approval'] || false)
          );
        }
        if (lineOfBusiness === 'Auto') {
          if (
            submissionType === 'irca' &&
            coverageBoundMethod === 'email_email'
          ) {
            return (
              checklistDataRef.current['auto_irca_email_approval'] || false
            );
          } else if (submissionType === 'fleet') {
            return checklistDataRef.current['auto_fleet_approval'] || false;
          } else if (submissionType === 'garage') {
            return checklistDataRef.current['auto_garage_approval'] || false;
          }
        }
        return false;
      };

      // Clear marketing approval fields if they're no longer relevant
      if (!isMarketingApprovalRelevant()) {
        if (marketingApprovalDate) {
          dispatch(setMarketingApprovalDate(''));
        }
        if (marketingApprovalTicket) {
          dispatch(setMarketingApprovalTicket(''));
        }
      }
    }

    prevSubmissionTypeRef.current = submissionType;
    prevCoverageBoundMethodRef.current = coverageBoundMethod;
  }, [
    submissionType,
    coverageBoundMethod,
    lineOfBusiness,
    dispatch,
    marketingApprovalDate,
    marketingApprovalTicket,
  ]);

  const handleChecklistChange = (id: string, checked: boolean) => {
    dispatch(setChecklistItem({ id, checked }));
  };

  const isChecked = (id: string): boolean => {
    return checklistData[id] || false;
  };

  const handleSubmissionTypeChange = (
    value: 'non_portal' | 'portal_phone' | 'irca' | 'fleet' | 'garage' | '',
  ) => {
    dispatch(setSubmissionType(value));
  };

  const handleCoverageBoundMethodChange = (value: string) => {
    dispatch(setCoverageBoundMethod(value));
  };

  // Get submission type options based on line of business
  const getSubmissionTypeOptions = () => {
    if (lineOfBusiness === 'Property & Casualty') {
      return [
        {
          value: 'non_portal',
          label:
            'Non-Portal Submission - Direct to Insurance Company/MGA Underwriter',
        },
        {
          value: 'portal_phone',
          label: 'Portal and/or Phone Submission - Quote to Bind',
        },
      ];
    } else if (lineOfBusiness === 'Auto') {
      return [
        { value: 'irca', label: 'IRCA (Individually Rated Commercial Auto)' },
        { value: 'fleet', label: 'Fleet' },
        { value: 'garage', label: 'Garage' },
      ];
    }
    return [];
  };

  const renderChecklistItems = () => {
    if (lineOfBusiness === 'Property & Casualty') {
      if (submissionType === 'non_portal') {
        return (
          <VStack align="stretch" spacing={3}>
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('pc_non_portal_approval')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'pc_non_portal_approval',
                      e.target.checked,
                    )
                  }
                >
                  BIG Commercial Marketing Approval on file
                </Checkbox>
              </FormLabel>
            </FormControl>
            {isChecked('pc_non_portal_approval') && (
              <>
                <FormControl isRequired>
                  <DateField
                    label="Date of approval"
                    date={getDateFromString(marketingApprovalDate || '')}
                    onChange={(date: Date | null) => {
                      dispatch(
                        setMarketingApprovalDate(
                          date ? getDateString(date) : '',
                        ),
                      );
                    }}
                    required
                    isDisabled={isLocked}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">
                    BIG Support Application Approval Ticket #
                  </FormLabel>
                  <Input
                    value={marketingApprovalTicket || ''}
                    onChange={(e) =>
                      dispatch(setMarketingApprovalTicket(e.target.value))
                    }
                    placeholder="Enter ticket number"
                    isDisabled={isLocked}
                    size="sm"
                  />
                </FormControl>
              </>
            )}
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('pc_non_portal_quote_signed')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'pc_non_portal_quote_signed',
                      e.target.checked,
                    )
                  }
                >
                  Quote signed by Client on file
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('pc_non_portal_correspondence')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'pc_non_portal_correspondence',
                      e.target.checked,
                    )
                  }
                >
                  All Correspondence on file – (With Client & Company/MGA
                  Underwriter) all emails sent and received, Notes for all
                  phone/in person conversations on file
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('pc_non_portal_building_valuations')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'pc_non_portal_building_valuations',
                      e.target.checked,
                    )
                  }
                >
                  Commercial Building Valuations on File (If building coverage
                  included)
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('pc_non_portal_pac_contract')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'pc_non_portal_pac_contract',
                      e.target.checked,
                    )
                  }
                >
                  Signed Monthly PAC or First Funding Contract and Void cheque
                  on file
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('pc_non_portal_application')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'pc_non_portal_application',
                      e.target.checked,
                    )
                  }
                >
                  BIG Commercial Application or Insurance Company/MGA Specific
                  Application on file
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('pc_non_portal_bind_request')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'pc_non_portal_bind_request',
                      e.target.checked,
                    )
                  }
                >
                  Bind request email to Insurance Company/MGA on file
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('pc_non_portal_bind_confirmation')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'pc_non_portal_bind_confirmation',
                      e.target.checked,
                    )
                  }
                >
                  Confirmation of bind email from Insurance Company/MGA
                  Underwriter (MGA/Non-Standard Insurance Companies)
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('pc_non_portal_quotes_declines')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'pc_non_portal_quotes_declines',
                      e.target.checked,
                    )
                  }
                >
                  Quotes and Declines from ALL Insurance Companies/MGAs attached
                  in file
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('pc_non_portal_abeyance')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'pc_non_portal_abeyance',
                      e.target.checked,
                    )
                  }
                >
                  New Abeyance Set for new policy documents to arrive in
                  Powerbroker
                </Checkbox>
              </FormLabel>
            </FormControl>
          </VStack>
        );
      } else if (submissionType === 'portal_phone') {
        return (
          <VStack align="stretch" spacing={3}>
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('pc_portal_quote_signed')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'pc_portal_quote_signed',
                      e.target.checked,
                    )
                  }
                >
                  Quote signed by Client on file
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('pc_portal_application')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'pc_portal_application',
                      e.target.checked,
                    )
                  }
                >
                  BIG Commercial Application OR Note in PowerBroker clearly
                  describing the client's business operations and risk
                  underwriting information
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('pc_portal_correspondence')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'pc_portal_correspondence',
                      e.target.checked,
                    )
                  }
                >
                  All Correspondence on file – (With Client & Company/MGA
                  Underwriter) all emails sent and received, Notes for all
                  phone/in person conversations on file
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('pc_portal_building_valuations')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'pc_portal_building_valuations',
                      e.target.checked,
                    )
                  }
                >
                  Commercial Building Valuations on File (If building coverage
                  included)
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('pc_portal_pac_contract')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'pc_portal_pac_contract',
                      e.target.checked,
                    )
                  }
                >
                  Signed Monthly PAC or First Funding Contract and Void cheque
                  on file
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('pc_portal_bind_confirmation')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'pc_portal_bind_confirmation',
                      e.target.checked,
                    )
                  }
                >
                  Confirmation of bind email from Insurance Company/MGA attached
                  in Powerbroker
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('pc_portal_abeyance')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'pc_portal_abeyance',
                      e.target.checked,
                    )
                  }
                >
                  New Abeyance Set for new policy documents to arrive in
                  Powerbroker
                </Checkbox>
              </FormLabel>
            </FormControl>
          </VStack>
        );
      }
    } else if (lineOfBusiness === 'Auto') {
      if (submissionType === 'irca') {
        if (
          coverageBoundMethod === 'applied_rating_email' ||
          coverageBoundMethod === 'applied_rating_portal'
        ) {
          return (
            <VStack align="stretch" spacing={3}>
              {!isCommercialAutoSigningEnabled && (
                <FormControl isRequired>
                  <FormLabel>
                    <Checkbox
                      isDisabled={isLocked}
                      isChecked={isChecked('auto_irca_light_oap1')}
                      onChange={(e) =>
                        handleChecklistChange(
                          'auto_irca_light_oap1',
                          e.target.checked,
                        )
                      }
                    >
                      Signed OAP1 (or Province Equivalent)
                    </Checkbox>
                  </FormLabel>
                </FormControl>
              )}
              {!isCommercialAutoSigningEnabled && (
                <FormControl isRequired>
                  <FormLabel>
                    <Checkbox
                      isDisabled={isLocked}
                      isChecked={isChecked('auto_irca_light_cvs')}
                      onChange={(e) =>
                        handleChecklistChange(
                          'auto_irca_light_cvs',
                          e.target.checked,
                        )
                      }
                    >
                      Signed Commercial Vehicle Supplement (CVS) or Province
                      Equivalent
                    </Checkbox>
                  </FormLabel>
                </FormControl>
              )}
              <FormControl isRequired>
                <FormLabel>
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_irca_light_ownerships')}
                    onChange={(e) =>
                      handleChecklistChange(
                        'auto_irca_light_ownerships',
                        e.target.checked,
                      )
                    }
                  >
                    Ownerships for ALL Vehicles
                  </Checkbox>
                </FormLabel>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_irca_light_mvr')}
                    onChange={(e) =>
                      handleChecklistChange(
                        'auto_irca_light_mvr',
                        e.target.checked,
                      )
                    }
                  >
                    MVR (Motor Vehicle Report) or Province Equivalent - Each
                    driver
                  </Checkbox>
                </FormLabel>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_irca_light_dash')}
                    onChange={(e) =>
                      handleChecklistChange(
                        'auto_irca_light_dash',
                        e.target.checked,
                      )
                    }
                  >
                    DASH Report (or Autoplus if applicable) - Each driver
                  </Checkbox>
                </FormLabel>
              </FormControl>
              <FormControl>
                <FormLabel>
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_irca_light_cvor')}
                    onChange={(e) =>
                      handleChecklistChange(
                        'auto_irca_light_cvor',
                        e.target.checked,
                      )
                    }
                  >
                    CVOR Level 2 or Province Equivalent - Heavy Vehicles Only
                  </Checkbox>
                </FormLabel>
              </FormControl>
              <FormControl>
                <FormLabel>
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_irca_light_loe')}
                    onChange={(e) =>
                      handleChecklistChange(
                        'auto_irca_light_loe',
                        e.target.checked,
                      )
                    }
                  >
                    Driver Letter of Experience
                  </Checkbox>
                </FormLabel>
              </FormControl>
              <FormControl>
                <FormLabel>
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_irca_light_bill_of_sale')}
                    onChange={(e) =>
                      handleChecklistChange(
                        'auto_irca_light_bill_of_sale',
                        e.target.checked,
                      )
                    }
                  >
                    Bill of Sale
                  </Checkbox>
                </FormLabel>
              </FormControl>
              <FormControl>
                <FormLabel>
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_irca_light_pac_contract')}
                    onChange={(e) =>
                      handleChecklistChange(
                        'auto_irca_light_pac_contract',
                        e.target.checked,
                      )
                    }
                  >
                    Signed Monthly PAC or First Funding Contract and Void cheque
                    on file
                  </Checkbox>
                </FormLabel>
              </FormControl>
              <FormControl isRequired>
                <FormLabel display="inline">
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_irca_light_correspondence')}
                    onChange={(e) =>
                      handleChecklistChange(
                        'auto_irca_light_correspondence',
                        e.target.checked,
                      )
                    }
                  >
                    All Correspondence on file – (With Client & Company/MGA
                    Underwriter) all emails sent and received, Notes for all
                    phone/in person conversations on file
                  </Checkbox>
                </FormLabel>
              </FormControl>
              {coverageBoundMethod === 'applied_rating_email' && (
                <>
                  <FormControl isRequired>
                    <FormLabel>
                      <Checkbox
                        isDisabled={isLocked}
                        isChecked={isChecked('auto_irca_light_bind_request')}
                        onChange={(e) =>
                          handleChecklistChange(
                            'auto_irca_light_bind_request',
                            e.target.checked,
                          )
                        }
                      >
                        Bind request email to Insurance Company on file
                      </Checkbox>
                    </FormLabel>
                  </FormControl>
                  <FormControl>
                    <FormLabel>
                      <Checkbox
                        isDisabled={isLocked}
                        isChecked={isChecked(
                          'auto_irca_light_bind_confirmation',
                        )}
                        onChange={(e) =>
                          handleChecklistChange(
                            'auto_irca_light_bind_confirmation',
                            e.target.checked,
                          )
                        }
                      >
                        Confirmation of bind email from Insurance Company
                        (Required for all MGA/Non-Standard Insurance Companies)
                      </Checkbox>
                    </FormLabel>
                  </FormControl>
                </>
              )}
              {coverageBoundMethod === 'applied_rating_portal' && (
                <FormControl isRequired>
                  <FormLabel>
                    <Checkbox
                      isDisabled={isLocked}
                      isChecked={isChecked(
                        'auto_irca_light_portal_confirmation',
                      )}
                      onChange={(e) =>
                        handleChecklistChange(
                          'auto_irca_light_portal_confirmation',
                          e.target.checked,
                        )
                      }
                    >
                      Confirmation of bind from Insurance Company portal
                      attached in Powerbroker
                    </Checkbox>
                  </FormLabel>
                </FormControl>
              )}
              {coverageBoundMethod === 'applied_rating_email' && (
                <FormControl>
                  <FormLabel>
                    <Checkbox
                      isDisabled={isLocked}
                      isChecked={isChecked('auto_irca_signed_monthly_pac')}
                      onChange={(e) =>
                        handleChecklistChange(
                          'auto_irca_signed_monthly_pac',
                          e.target.checked,
                        )
                      }
                    >
                      Signed Monthly PAC or First Funding Contract and Void
                      cheque on file
                    </Checkbox>
                  </FormLabel>
                </FormControl>
              )}
              <FormControl isRequired>
                <FormLabel>
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_irca_light_abeyance')}
                    onChange={(e) =>
                      handleChecklistChange(
                        'auto_irca_light_abeyance',
                        e.target.checked,
                      )
                    }
                  >
                    New Abeyance Set for new documents to arrive in Powerbroker
                  </Checkbox>
                </FormLabel>
              </FormControl>
            </VStack>
          );
        } else if (coverageBoundMethod === 'email_email') {
          return (
            <VStack align="stretch" spacing={3}>
              <FormControl isRequired>
                <FormLabel>
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_irca_email_approval')}
                    onChange={(e) =>
                      handleChecklistChange(
                        'auto_irca_email_approval',
                        e.target.checked,
                      )
                    }
                  >
                    BIG Commercial Marketing Approval on file
                  </Checkbox>
                </FormLabel>
              </FormControl>
              {isChecked('auto_irca_email_approval') && (
                <>
                  <FormControl isRequired>
                    <DateField
                      label="Date of approval"
                      date={getDateFromString(marketingApprovalDate || '')}
                      onChange={(date: Date | null) => {
                        dispatch(
                          setMarketingApprovalDate(
                            date ? getDateString(date) : '',
                          ),
                        );
                      }}
                      required
                      isDisabled={isLocked}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">
                      BIG Support Application Approval Ticket #
                    </FormLabel>
                    <Input
                      value={marketingApprovalTicket || ''}
                      onChange={(e) =>
                        dispatch(setMarketingApprovalTicket(e.target.value))
                      }
                      placeholder="Enter ticket number"
                      isDisabled={isLocked}
                      size="sm"
                    />
                  </FormControl>
                </>
              )}
              {!isCommercialAutoSigningEnabled && (
                <FormControl isRequired>
                  <FormLabel>
                    <Checkbox
                      isDisabled={isLocked}
                      isChecked={isChecked('auto_irca_email_oap1')}
                      onChange={(e) =>
                        handleChecklistChange(
                          'auto_irca_email_oap1',
                          e.target.checked,
                        )
                      }
                    >
                      Signed OAP1 (or Province Equivalent)
                    </Checkbox>
                  </FormLabel>
                </FormControl>
              )}
              {!isCommercialAutoSigningEnabled && (
                <FormControl isRequired>
                  <FormLabel>
                    <Checkbox
                      isDisabled={isLocked}
                      isChecked={isChecked('auto_irca_email_cvs')}
                      onChange={(e) =>
                        handleChecklistChange(
                          'auto_irca_email_cvs',
                          e.target.checked,
                        )
                      }
                    >
                      Signed Commercial Vehicle Supplement (CVS) or Province
                      Equivalent
                    </Checkbox>
                  </FormLabel>
                </FormControl>
              )}
              <FormControl isRequired>
                <FormLabel>
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_irca_email_ownerships')}
                    onChange={(e) =>
                      handleChecklistChange(
                        'auto_irca_email_ownerships',
                        e.target.checked,
                      )
                    }
                  >
                    Ownerships for ALL Vehicles
                  </Checkbox>
                </FormLabel>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_irca_email_mvr')}
                    onChange={(e) =>
                      handleChecklistChange(
                        'auto_irca_email_mvr',
                        e.target.checked,
                      )
                    }
                  >
                    MVR (Motor Vehicle Report) or Province Equivalent - Each
                    driver
                  </Checkbox>
                </FormLabel>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_irca_email_dash')}
                    onChange={(e) =>
                      handleChecklistChange(
                        'auto_irca_email_dash',
                        e.target.checked,
                      )
                    }
                  >
                    DASH Report (or Autoplus if applicable) - Each driver
                  </Checkbox>
                </FormLabel>
              </FormControl>
              <FormControl>
                <FormLabel>
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_irca_email_cvor')}
                    onChange={(e) =>
                      handleChecklistChange(
                        'auto_irca_email_cvor',
                        e.target.checked,
                      )
                    }
                  >
                    CVOR Level 2 or Province Equivalent - Heavy Vehicles Only
                  </Checkbox>
                </FormLabel>
              </FormControl>
              <FormControl>
                <FormLabel>
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_irca_email_loe')}
                    onChange={(e) =>
                      handleChecklistChange(
                        'auto_irca_email_loe',
                        e.target.checked,
                      )
                    }
                  >
                    Letter of Experience
                  </Checkbox>
                </FormLabel>
              </FormControl>
              <FormControl>
                <FormLabel>
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_irca_email_bill_of_sale')}
                    onChange={(e) =>
                      handleChecklistChange(
                        'auto_irca_email_bill_of_sale',
                        e.target.checked,
                      )
                    }
                  >
                    Bill of Sale
                  </Checkbox>
                </FormLabel>
              </FormControl>
              <FormControl>
                <FormLabel>
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_irca_email_pac_contract')}
                    onChange={(e) =>
                      handleChecklistChange(
                        'auto_irca_email_pac_contract',
                        e.target.checked,
                      )
                    }
                  >
                    Signed Monthly PAC or First Funding Contract and Void cheque
                    on file
                  </Checkbox>
                </FormLabel>
              </FormControl>
              <FormControl isRequired>
                <FormLabel display="inline">
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_irca_email_correspondence')}
                    onChange={(e) =>
                      handleChecklistChange(
                        'auto_irca_email_correspondence',
                        e.target.checked,
                      )
                    }
                  >
                    All Correspondence on file – (With Client & Company/MGA
                    Underwriter) all emails sent and received, Notes for all
                    phone/in person conversations on file
                  </Checkbox>
                </FormLabel>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_irca_email_bind_request')}
                    onChange={(e) =>
                      handleChecklistChange(
                        'auto_irca_email_bind_request',
                        e.target.checked,
                      )
                    }
                  >
                    Bind request email to Insurance Company on file
                  </Checkbox>
                </FormLabel>
              </FormControl>
              <FormControl>
                <FormLabel>
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_irca_email_bind_confirmation')}
                    onChange={(e) =>
                      handleChecklistChange(
                        'auto_irca_email_bind_confirmation',
                        e.target.checked,
                      )
                    }
                  >
                    Confirmation of bind email from Insurance Company (Required
                    for all MGA/Non-Standard Insurance Companies)
                  </Checkbox>
                </FormLabel>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_irca_email_abeyance')}
                    onChange={(e) =>
                      handleChecklistChange(
                        'auto_irca_email_abeyance',
                        e.target.checked,
                      )
                    }
                  >
                    New Abeyance Set for new documents to arrive in Powerbroker
                  </Checkbox>
                </FormLabel>
              </FormControl>
            </VStack>
          );
        }
      } else if (submissionType === 'fleet') {
        return (
          <VStack align="stretch" spacing={3}>
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_fleet_approval')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'auto_fleet_approval',
                      e.target.checked,
                    )
                  }
                >
                  BIG Commercial Marketing Approval on file
                </Checkbox>
              </FormLabel>
            </FormControl>
            {isChecked('auto_fleet_approval') && (
              <>
                <FormControl isRequired>
                  <DateField
                    label="Date of approval"
                    date={getDateFromString(marketingApprovalDate || '')}
                    onChange={(date: Date | null) => {
                      dispatch(
                        setMarketingApprovalDate(
                          date ? getDateString(date) : '',
                        ),
                      );
                    }}
                    required
                    isDisabled={isLocked}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">
                    BIG Support Application Approval Ticket #
                  </FormLabel>
                  <Input
                    value={marketingApprovalTicket || ''}
                    onChange={(e) =>
                      dispatch(setMarketingApprovalTicket(e.target.value))
                    }
                    placeholder="Enter ticket number"
                    isDisabled={isLocked}
                    size="sm"
                  />
                </FormControl>
              </>
            )}
            {!isCommercialAutoSigningEnabled && (
              <FormControl isRequired>
                <FormLabel>
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_fleet_oap1')}
                    onChange={(e) =>
                      handleChecklistChange('auto_fleet_oap1', e.target.checked)
                    }
                  >
                    Signed OAP1 (or Province Equivalent) with fleet and driver
                    schedule enclosed
                  </Checkbox>
                </FormLabel>
              </FormControl>
            )}
            {!isCommercialAutoSigningEnabled && (
              <FormControl isRequired>
                <FormLabel>
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_fleet_cvs')}
                    onChange={(e) =>
                      handleChecklistChange('auto_fleet_cvs', e.target.checked)
                    }
                  >
                    Signed Commercial Vehicle Supplement (CVS) or Province
                    Equivalent
                  </Checkbox>
                </FormLabel>
              </FormControl>
            )}
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_fleet_ownerships')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'auto_fleet_ownerships',
                      e.target.checked,
                    )
                  }
                >
                  Ownerships for ALL Vehicles
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_fleet_mvr')}
                  onChange={(e) =>
                    handleChecklistChange('auto_fleet_mvr', e.target.checked)
                  }
                >
                  MVR (Motor Vehicle Report) or Province Equivalent - Each
                  driver
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_fleet_dash')}
                  onChange={(e) =>
                    handleChecklistChange('auto_fleet_dash', e.target.checked)
                  }
                >
                  DASH Report (or Autoplus if applicable) - Each driver
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_fleet_cvor')}
                  onChange={(e) =>
                    handleChecklistChange('auto_fleet_cvor', e.target.checked)
                  }
                >
                  CVOR Level 2 or Province Equivalent - Heavy Vehicles Only
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_fleet_fleet_loe')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'auto_fleet_fleet_loe',
                      e.target.checked,
                    )
                  }
                >
                  Fleet Letter of Experience
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_fleet_driver_loe')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'auto_fleet_driver_loe',
                      e.target.checked,
                    )
                  }
                >
                  Driver Letter of Experience
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_fleet_bill_of_sale')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'auto_fleet_bill_of_sale',
                      e.target.checked,
                    )
                  }
                >
                  Bill of Sale
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_fleet_pac_contract')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'auto_fleet_pac_contract',
                      e.target.checked,
                    )
                  }
                >
                  Signed Monthly PAC or First Funding Contract and Void cheque
                  on file
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl isRequired>
              <FormLabel display="inline">
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_fleet_correspondence')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'auto_fleet_correspondence',
                      e.target.checked,
                    )
                  }
                >
                  All Correspondence on file – (With Client & Company/MGA
                  Underwriter) all emails sent and received, Notes for all
                  phone/in person conversations on file
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_fleet_bind_request')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'auto_fleet_bind_request',
                      e.target.checked,
                    )
                  }
                >
                  Bind request email to Insurance Company on file
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_fleet_bind_confirmation')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'auto_fleet_bind_confirmation',
                      e.target.checked,
                    )
                  }
                >
                  Confirmation of bind email from Insurance Company (Required
                  for all MGA/Non-Standard Insurance Companies)
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_fleet_abeyance')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'auto_fleet_abeyance',
                      e.target.checked,
                    )
                  }
                >
                  New Abeyance Set for new documents to arrive in Powerbroker
                </Checkbox>
              </FormLabel>
            </FormControl>
          </VStack>
        );
      } else if (submissionType === 'garage') {
        return (
          <VStack align="stretch" spacing={3}>
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_garage_approval')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'auto_garage_approval',
                      e.target.checked,
                    )
                  }
                >
                  BIG Commercial Marketing Approval on file
                </Checkbox>
              </FormLabel>
            </FormControl>
            {isChecked('auto_garage_approval') && (
              <>
                <FormControl isRequired>
                  <DateField
                    label="Date of approval"
                    date={getDateFromString(marketingApprovalDate || '')}
                    onChange={(date: Date | null) => {
                      dispatch(
                        setMarketingApprovalDate(
                          date ? getDateString(date) : '',
                        ),
                      );
                    }}
                    required
                    isDisabled={isLocked}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">
                    BIG Support Application Approval Ticket #
                  </FormLabel>
                  <Input
                    value={marketingApprovalTicket || ''}
                    onChange={(e) =>
                      dispatch(setMarketingApprovalTicket(e.target.value))
                    }
                    placeholder="Enter ticket number"
                    isDisabled={isLocked}
                    size="sm"
                  />
                </FormControl>
              </>
            )}
            {!isCommercialAutoSigningEnabled && (
              <FormControl isRequired>
                <FormLabel>
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_garage_oap4')}
                    onChange={(e) =>
                      handleChecklistChange(
                        'auto_garage_oap4',
                        e.target.checked,
                      )
                    }
                  >
                    Signed OAP4 (or Province Equivalent)
                  </Checkbox>
                </FormLabel>
              </FormControl>
            )}
            {!isCommercialAutoSigningEnabled && (
              <FormControl isRequired>
                <FormLabel>
                  <Checkbox
                    isDisabled={isLocked}
                    isChecked={isChecked('auto_garage_supplement')}
                    onChange={(e) =>
                      handleChecklistChange(
                        'auto_garage_supplement',
                        e.target.checked,
                      )
                    }
                  >
                    Signed Garage Supplement
                  </Checkbox>
                </FormLabel>
              </FormControl>
            )}
            <FormControl>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_garage_questionnaire')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'auto_garage_questionnaire',
                      e.target.checked,
                    )
                  }
                >
                  Used Car Dealer Questionnaire (If applicable)
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_garage_dealer_plate')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'auto_garage_dealer_plate',
                      e.target.checked,
                    )
                  }
                >
                  Service/Dealer Plate ownership
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_garage_ownerships')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'auto_garage_ownerships',
                      e.target.checked,
                    )
                  }
                >
                  Ownerships for ALL Vehicles
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_garage_mvr')}
                  onChange={(e) =>
                    handleChecklistChange('auto_garage_mvr', e.target.checked)
                  }
                >
                  MVR (Motor Vehicle Report) or Province Equivalent - Each
                  driver
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_garage_dash')}
                  onChange={(e) =>
                    handleChecklistChange('auto_garage_dash', e.target.checked)
                  }
                >
                  DASH Report - Each driver
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_garage_garage_loe')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'auto_garage_garage_loe',
                      e.target.checked,
                    )
                  }
                >
                  Garage Letter of Experience
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_garage_driver_loe')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'auto_garage_driver_loe',
                      e.target.checked,
                    )
                  }
                >
                  Driver Letter of Experience
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_garage_pac_contract')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'auto_garage_pac_contract',
                      e.target.checked,
                    )
                  }
                >
                  Signed Monthly PAC or First Funding Contract and Void cheque
                  on file
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl isRequired>
              <FormLabel display="inline">
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_garage_correspondence')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'auto_garage_correspondence',
                      e.target.checked,
                    )
                  }
                >
                  All Correspondence on file – (With Client & Company/MGA
                  Underwriter) all emails sent and received, Notes for all
                  phone/in person conversations on file
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_garage_bind_request')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'auto_garage_bind_request',
                      e.target.checked,
                    )
                  }
                >
                  Bind request email to Insurance Company on file
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_garage_bind_confirmation')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'auto_garage_bind_confirmation',
                      e.target.checked,
                    )
                  }
                >
                  Confirmation of bind email from Insurance Company (Required
                  for all MGA/Non-Standard Insurance Companies)
                </Checkbox>
              </FormLabel>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Checkbox
                  isDisabled={isLocked}
                  isChecked={isChecked('auto_garage_abeyance')}
                  onChange={(e) =>
                    handleChecklistChange(
                      'auto_garage_abeyance',
                      e.target.checked,
                    )
                  }
                >
                  New Abeyance Set for new documents to arrive in Powerbroker
                </Checkbox>
              </FormLabel>
            </FormControl>
          </VStack>
        );
      }
    }
    return null;
  };

  return (
    <Card mb="20px" id="sectionCommercialChecklist">
      <Text fontWeight="bold" fontSize="2xl" mb="20px">
        {isCommercialAutoSigningEnabled
          ? 'Broker File Documentation Requirements Checklist'
          : 'Submission Requirements Checklist'}
      </Text>
      <VStack spacing={4} align="stretch">
        <FormControl isRequired isInvalid={!!validationErrors?.submission_type}>
          <FormLabel ml="0">Submission Type</FormLabel>
          <RadioGroup
            value={submissionType}
            onChange={handleSubmissionTypeChange}
            isDisabled={isLocked}
          >
            <VStack align="stretch" spacing={2}>
              {getSubmissionTypeOptions().map((option) => (
                <Radio
                  key={option.value}
                  value={option.value}
                  isDisabled={isLocked}
                >
                  {option.label}
                </Radio>
              ))}
            </VStack>
          </RadioGroup>
          {validationErrors?.submission_type && (
            <FormErrorMessage>
              {validationErrors.submission_type}
            </FormErrorMessage>
          )}
        </FormControl>

        {/* Coverage Bound Method - shown for Portal/Phone submissions */}
        {lineOfBusiness === 'Property & Casualty' &&
          submissionType === 'portal_phone' && (
            <FormControl
              isRequired
              isInvalid={!!validationErrors?.coverage_bound_method}
            >
              <FormLabel ml="0">
                {isCommercialAutoSigningEnabled
                  ? 'How would the coverage be bound?'
                  : 'How was coverage bound?'}
              </FormLabel>
              <RadioGroup
                value={coverageBoundMethod}
                onChange={handleCoverageBoundMethodChange}
                isDisabled={isLocked}
              >
                <VStack align="stretch" spacing={2}>
                  <Radio value="quoted_by_phone" isDisabled={isLocked}>
                    Quoted by phone (USLI Only)/Bound via email to Insurance
                    Company/MGA underwriter
                  </Radio>
                  <Radio value="quoted_on_portal" isDisabled={isLocked}>
                    Quoted on Portal/Bound via email to Insurance Company/MGA
                    underwriter
                  </Radio>
                  <Radio value="quoted_bound_on_portal" isDisabled={isLocked}>
                    Quoted/Bound on Insurance Company/MGA Portal
                  </Radio>
                </VStack>
              </RadioGroup>
              {validationErrors?.coverage_bound_method && (
                <FormErrorMessage>
                  {validationErrors.coverage_bound_method}
                </FormErrorMessage>
              )}
            </FormControl>
          )}

        {/* Coverage Bound Method - shown for IRCA Auto */}
        {lineOfBusiness === 'Auto' && submissionType === 'irca' && (
          <FormControl
            isRequired
            isInvalid={!!validationErrors?.coverage_bound_method}
          >
            <FormLabel ml="0">
              {isCommercialAutoSigningEnabled
                ? 'How would the coverage be bound?'
                : 'How was coverage bound?'}
            </FormLabel>
            <RadioGroup
              value={coverageBoundMethod}
              onChange={handleCoverageBoundMethodChange}
              isDisabled={isLocked}
            >
              <VStack align="stretch" spacing={2}>
                <Radio value="applied_rating_email" isDisabled={isLocked}>
                  Quoted on Applied Rating Services (Light IRCA Only) and bound
                  via email to Insurance Company/MGA Underwriter
                </Radio>
                <Radio value="applied_rating_portal" isDisabled={isLocked}>
                  Quoted on Applied Rating Services (Light IRCA Only) and bound
                  on Insurance Company/MGA Portal
                </Radio>
                <Radio value="email_email" isDisabled={isLocked}>
                  Quoted by email with Insurance Company/MGA Underwriter and
                  Bound via email to Insurance Company/MGA Underwriter
                </Radio>
              </VStack>
            </RadioGroup>
            {validationErrors?.coverage_bound_method && (
              <FormErrorMessage>
                {validationErrors.coverage_bound_method}
              </FormErrorMessage>
            )}
          </FormControl>
        )}
        <br />
        <Box mt={8}>{renderChecklistItems()}</Box>
      </VStack>
    </Card>
  );
};

export default CommercialChecklist;
